/**
 * MatchHistoryView.tsx
 *
 * Displays every matched reconciliation group for the active company and lets
 * the user break any match group with a single click.
 *
 * Supports both:
 *   • Legacy 1:1 pairs (synthetic single-element groups)
 *   • M:N groups (multiple bank txs ↔ multiple expected actions)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Unlink,
  Loader2,
  AlertCircle,
  History,
  ArrowLeftRight,
  Search,
  RefreshCw,
  CheckCircle2,
  Building2,
  Tag,
  Users,
} from 'lucide-react';
import {
  reconciliationService,
  MatchedGroup,
} from '../../lib/reconciliationService';
import { emitToast } from '../ui/Toast';
import { formatDateSafe } from '../../lib/dateUtils';

// ─── helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d: string) => formatDateSafe(d);

const fmtAmt = (n: number) =>
  new Intl.NumberFormat('he-IL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const amtCls = (n: number) => (n >= 0 ? 'text-green-600' : 'text-red-600');

const TYPE_BADGE: Record<string, string> = {
  recurring: 'bg-purple-100 text-purple-800',
  known:     'bg-blue-100 text-blue-800',
};

const TYPE_LABEL: Record<string, string> = {
  recurring: 'פעולה קבועה',
  known:     'פעולה ידועה',
};

// ─── props ────────────────────────────────────────────────────────────────────

interface MatchHistoryViewProps {
  companyId: string;
}

// ─── component ────────────────────────────────────────────────────────────────

export function MatchHistoryView({ companyId }: MatchHistoryViewProps) {
  const [groups, setGroups]     = useState<MatchedGroup[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [search, setSearch]     = useState('');
  const [undoing, setUndoing]   = useState<Set<string>>(new Set()); // keyed by groupId

  // ── load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await reconciliationService.getMatchedTransactions(companyId);
    if (err) {
      setError(err.message || 'שגיאה בטעינת היסטוריית התאמות');
    } else {
      setGroups(data ?? []);
    }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // ── filtered list ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(({ bankTxs, actions }) => {
      const bankFields = bankTxs.flatMap((tx) => [
        tx.description,
        tx.bank_accounts?.nickname ?? '',
        tx.transaction_date,
        String(tx.amount),
      ]);
      const actionFields = actions.flatMap((a) => [
        a.description,
        a.bank_accounts?.nickname ?? '',
        a.accounting_categories?.name ?? '',
        a.action_date,
        String(a.amount),
        TYPE_LABEL[a.type] ?? '',
      ]);
      return [...bankFields, ...actionFields].some((f) => f.toLowerCase().includes(q));
    });
  }, [groups, search]);

  // ── undo ──────────────────────────────────────────────────────────────────
  const handleUndo = async (group: MatchedGroup) => {
    const key = group.groupId;
    setUndoing((prev) => new Set(prev).add(key));

    const { error: err } = await reconciliationService.undoGroup(group.groupId);

    setUndoing((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

    if (err) {
      emitToast(err.message || 'שגיאה בביטול ההתאמה', 'error');
    } else {
      const bankCount = group.bankTxs.length;
      const actCount  = group.actions.length;
      const msg =
        bankCount === 1 && actCount === 1
          ? 'ההתאמה בוטלה בהצלחה — הפעולות חזרו לתור ההמתנה'
          : `ביטול קבוצת התאמה: ${bankCount} תנועות בנק ו-${actCount} פעולות חזרו לממתינות`;
      emitToast(msg, 'success');
      setGroups((prev) => prev.filter((g) => g.groupId !== key));
    }
  };

  // ── loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // ── error state ───────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <AlertCircle className="h-10 w-10 text-red-400" />
        <p className="text-red-700 text-sm">{error}</p>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white
            bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          נסה שוב
        </button>
      </div>
    );
  }

  // ── empty state ───────────────────────────────────────────────────────────
  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="p-5 bg-gray-100 rounded-full">
          <History className="h-10 w-10 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-700">אין התאמות להצגה</h3>
        <p className="text-sm text-gray-500 text-center max-w-sm">
          ברגע שתתאים תנועות בנק לפעולות קבועות או ידועות, הן יופיעו כאן לביקורת.
        </p>
      </div>
    );
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-4" dir="rtl">

      {/* ── Header row ───────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full
            text-sm font-medium bg-indigo-100 text-indigo-800">
            <CheckCircle2 className="h-4 w-4" />
            {groups.length} התאמות
          </span>
          {/* Count of M:N groups */}
          {groups.filter((g) => g.bankTxs.length > 1 || g.actions.length > 1).length > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full
              text-sm font-medium bg-amber-100 text-amber-800">
              <Users className="h-4 w-4" />
              {groups.filter((g) => g.bankTxs.length > 1 || g.actions.length > 1).length} קבוצות מרובות
            </span>
          )}
          {search && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full
              text-sm font-medium bg-gray-100 text-gray-700">
              {filtered.length} תוצאות לחיפוש
            </span>
          )}
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300
            rounded-lg text-sm font-medium text-gray-600 bg-white hover:bg-gray-50
            transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          רענן
        </button>
      </div>

      {/* ── Search bar ───────────────────────────────────────────────────── */}
      <div className="relative">
        <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="חיפוש לפי תיאור, חשבון, סכום, קטגוריה..."
          className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg text-sm
            focus:ring-2 focus:ring-primary-500 focus:border-transparent text-right"
        />
      </div>

      {/* ── No search results ─────────────────────────────────────────────── */}
      {filtered.length === 0 && (
        <div className="py-12 text-center text-gray-500 text-sm">
          לא נמצאו תוצאות עבור &quot;{search}&quot;
        </div>
      )}

      {/* ── Group cards ──────────────────────────────────────────────────── */}
      {filtered.length > 0 && (
        <div className="flex flex-col gap-3">
          {filtered.map((group) => {
            const isUndoing  = undoing.has(group.groupId);
            const isMultiple = group.bankTxs.length > 1 || group.actions.length > 1;

            const bankSum   = group.bankTxs.reduce((s, t) => s + t.amount, 0);
            const actionSum = group.actions.reduce((s, a) => s + a.amount, 0);

            return (
              <div
                key={group.groupId}
                className={`bg-white border rounded-xl shadow-sm overflow-hidden
                  ${isMultiple ? 'border-amber-200' : 'border-gray-200'}`}
              >
                {/* Group header bar */}
                <div className={`flex items-center justify-between px-4 py-2
                  ${isMultiple ? 'bg-amber-50 border-b border-amber-100' : 'bg-gray-50 border-b border-gray-100'}`}
                >
                  <div className="flex items-center gap-2">
                    {isMultiple && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                        text-xs font-semibold bg-amber-100 text-amber-800">
                        <Users className="h-3 w-3" />
                        {group.bankTxs.length} × {group.actions.length}
                      </span>
                    )}
                    <span className="text-xs text-gray-500">
                      {group.bankTxs.length === 1
                        ? fmtDate(group.bankTxs[0]?.transaction_date ?? '')
                        : `${fmtDate(group.bankTxs[group.bankTxs.length - 1]?.transaction_date ?? '')} – ${fmtDate(group.bankTxs[0]?.transaction_date ?? '')}`}
                    </span>
                  </div>
                  <button
                    onClick={() => handleUndo(group)}
                    disabled={isUndoing}
                    title="בטל התאמה"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-200
                      text-red-600 bg-red-50 rounded-lg text-xs font-medium
                      hover:bg-red-100 hover:border-red-300
                      disabled:opacity-50 disabled:cursor-not-allowed
                      transition-colors"
                  >
                    {isUndoing
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Unlink className="h-3.5 w-3.5" />}
                    {isUndoing ? 'מבטל...' : 'בטל התאמה'}
                  </button>
                </div>

                {/* Two-column body: bank side | arrow | action side */}
                <div className="grid grid-cols-[1fr_auto_1fr] gap-0 divide-x divide-x-reverse divide-gray-100">

                  {/* ── Bank side ─────────────────────────────────────── */}
                  <div className="px-4 py-3">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                      תנועות בנק ({group.bankTxs.length})
                    </p>
                    <div className="flex flex-col gap-2">
                      {group.bankTxs.map((tx) => (
                        <div key={tx.id} className="flex flex-col gap-0.5">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="text-sm font-medium text-gray-900 truncate">{tx.description}</span>
                            <span className={`text-sm font-semibold flex-shrink-0 ${amtCls(tx.amount)}`}>
                              {fmtAmt(tx.amount)} ₪
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-400">{fmtDate(tx.transaction_date)}</span>
                            {tx.bank_accounts?.nickname && (
                              <span className="inline-flex items-center gap-0.5 text-xs text-gray-500">
                                <Building2 className="h-3 w-3 flex-shrink-0" />
                                {tx.bank_accounts.nickname}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {group.bankTxs.length > 1 && (
                      <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between text-xs text-gray-500">
                        <span>סה&quot;כ</span>
                        <span className={`font-semibold ${amtCls(bankSum)}`}>{fmtAmt(bankSum)} ₪</span>
                      </div>
                    )}
                  </div>

                  {/* ── Arrow ────────────────────────────────────────── */}
                  <div className="flex items-center justify-center px-3">
                    <ArrowLeftRight className="h-4 w-4 text-gray-300" />
                  </div>

                  {/* ── Action side ───────────────────────────────────── */}
                  <div className="px-4 py-3">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                      פעולות צפויות ({group.actions.length})
                    </p>
                    <div className="flex flex-col gap-2">
                      {group.actions.map((action) => (
                        <div key={action.id} className="flex flex-col gap-0.5">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="text-sm font-medium text-gray-900 truncate">{action.description}</span>
                            <span className={`text-sm font-semibold flex-shrink-0 ${amtCls(action.amount)}`}>
                              {fmtAmt(action.amount)} ₪
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs text-gray-400">{fmtDate(action.action_date)}</span>
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium
                              ${TYPE_BADGE[action.type] ?? 'bg-gray-100 text-gray-700'}`}>
                              {TYPE_LABEL[action.type] ?? action.type}
                            </span>
                            {action.accounting_categories?.name && (
                              <span className="inline-flex items-center gap-0.5 text-xs text-gray-500">
                                <Tag className="h-3 w-3 flex-shrink-0" />
                                {action.accounting_categories.name}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {group.actions.length > 1 && (
                      <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between text-xs text-gray-500">
                        <span>סה&quot;כ</span>
                        <span className={`font-semibold ${amtCls(actionSum)}`}>{fmtAmt(actionSum)} ₪</span>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
